import React, { useRef, useEffect, useCallback, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const BAR_HEIGHT = 14;

// Matches the fixed mobile top bar's height (see ProtectedRoute's pt-14) so
// the scrollbar sits just below it instead of under it; no such bar exists
// on desktop.
const getStickyOffset = () => (window.innerWidth < 768 ? 56 : 0);

/**
 * Wraps wide content (a table, a wide grid) that needs horizontal
 * scrolling with a second, thin scrollbar pinned above it and kept in
 * sync with the real one below - so on a long list you don't have to
 * scroll all the way down just to reach the horizontal scrollbar.
 *
 * The strip is positioned like `position: sticky` (frozen near the top of
 * the viewport while the table is scrolled through, released once the
 * table's bottom passes it) but computed manually and rendered through a
 * portal straight into <body>, rather than actual CSS sticky - every real
 * usage sits inside a `.glass-card`, which uses `backdrop-filter`, and
 * `position: sticky` is unreliable (in some browsers doesn't stick at all)
 * inside a `filter`/`backdrop-filter` ancestor. Rendering outside that
 * ancestor via a portal sidesteps the problem entirely.
 *
 * Usage: replace `overflow-x-auto` on the wrapping div with <ScrollX> -
 * ScrollX supplies its own overflow-x-auto on both the strip and the
 * actual content, so any other classes (glass-card, border, rounded, mb-6,
 * etc.) stay on the outer div as before.
 */
const ScrollX = ({ children }) => {
  const bottomRef = useRef(null);
  const barRef = useRef(null);
  const spacerRef = useRef(null);
  const syncingFrom = useRef(null);
  const [barStyle, setBarStyle] = useState(null);

  const syncSpacerWidth = useCallback(() => {
    if (bottomRef.current && spacerRef.current) {
      spacerRef.current.style.width = `${bottomRef.current.scrollWidth}px`;
    }
  }, []);

  const updateBarPosition = useCallback(() => {
    const el = bottomRef.current;
    if (!el) return;
    // Nothing to scroll horizontally - don't render the strip at all.
    // Previously this rendered unconditionally at a fixed, portal-computed
    // position regardless of whether the content actually overflowed, so a
    // table narrower than its container still got a floating 14px bar
    // sitting wherever the table's top happened to be - visible as a stray
    // horizontal bar cutting across the header row (2026-09-01 bug report:
    // it was covering the Cash Flow module's Accounts page inputs).
    if (el.scrollWidth <= el.clientWidth + 1) {
      setBarStyle(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const offset = getStickyOffset();
    let top;
    if (rect.top >= offset) {
      top = rect.top; // table hasn't scrolled into the frozen range yet
    } else if (rect.bottom <= offset + BAR_HEIGHT) {
      top = rect.bottom - BAR_HEIGHT; // table has scrolled past - release, same as CSS sticky
    } else {
      top = offset; // frozen
    }
    setBarStyle({ top, left: rect.left, width: rect.width });
  }, []);

  useLayoutEffect(() => {
    updateBarPosition();
  }, [updateBarPosition]);

  useEffect(() => {
    syncSpacerWidth();
    const el = bottomRef.current;
    const onScrollOrResize = () => {
      syncSpacerWidth();
      updateBarPosition();
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    let ro;
    let mo;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onScrollOrResize);
      ro.observe(el);
    }
    if (el && typeof MutationObserver !== 'undefined') {
      // Content (e.g. table columns/rows) can change size without the
      // container itself resizing, which also shifts where the table's
      // bottom edge is - re-check both on any DOM change inside it.
      mo = new MutationObserver(onScrollOrResize);
      mo.observe(el, { childList: true, subtree: true });
    }
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
    };
  }, [syncSpacerWidth, updateBarPosition]);

  const handleBarScroll = () => {
    if (syncingFrom.current === 'bottom') { syncingFrom.current = null; return; }
    syncingFrom.current = 'top';
    if (bottomRef.current && barRef.current) {
      bottomRef.current.scrollLeft = barRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (syncingFrom.current === 'top') { syncingFrom.current = null; return; }
    syncingFrom.current = 'bottom';
    if (barRef.current && bottomRef.current) {
      barRef.current.scrollLeft = bottomRef.current.scrollLeft;
    }
  };

  return (
    <div>
      {/* Reserves the strip's height in normal layout flow - the real,
          interactive scrollbar is portal-rendered below. */}
      <div style={{ height: BAR_HEIGHT }} />
      {barStyle && createPortal(
        <div
          ref={barRef}
          onScroll={handleBarScroll}
          className="fixed z-20 overflow-x-auto overflow-y-hidden bg-white/90 backdrop-blur-sm border-b border-gray-200"
          style={{ height: BAR_HEIGHT, top: barStyle.top, left: barStyle.left, width: barStyle.width }}
        >
          <div ref={spacerRef} style={{ height: 1 }} />
        </div>,
        document.body
      )}
      <div ref={bottomRef} onScroll={handleBottomScroll} className="overflow-x-auto">
        {children}
      </div>
    </div>
  );
};

export default ScrollX;
